import { useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import { Download, Unlock, AlertCircle } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Dropzone } from '@/components/ui/Dropzone';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { readFileAsArrayBuffer, bytesToBlob } from '@/lib/utils';
import { pdfjs } from '@/lib/pdfjs';

export function PdfUnlock() {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [working, setWorking] = useState(false);
  const [err, setErr] = useState('');

  async function unlock() {
    if (!file) return;
    setWorking(true);
    setErr('');
    try {
      const buf = await readFileAsArrayBuffer(file);
      // Use pdfjs to extract pages with password, then rebuild a clean copy.
      const src = await pdfjs.getDocument({ data: buf, password }).promise;
      const out = await PDFDocument.create();

      for (let i = 1; i <= src.numPages; i++) {
        const page = await src.getPage(i);
        const viewport = page.getViewport({ scale: 2 });
        const c = document.createElement('canvas');
        c.width = viewport.width;
        c.height = viewport.height;
        const ctx = c.getContext('2d')!;
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, c.width, c.height);
        await page.render({ canvasContext: ctx, viewport, canvas: c }).promise;
        const dataUrl = c.toDataURL('image/jpeg', 0.92);
        const jpgBytes = await fetch(dataUrl).then((r) => r.arrayBuffer());
        const embedded = await out.embedJpg(jpgBytes);
        const newPage = out.addPage([viewport.width, viewport.height]);
        newPage.drawImage(embedded, { x: 0, y: 0, width: viewport.width, height: viewport.height });
      }

      const bytes = await out.save();
      saveAs(bytesToBlob(bytes), file.name.replace(/\.pdf$/i, '') + '-unlocked.pdf');
      toast.success('Unlocked');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not unlock');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="pdf-unlock"
        title="Unlock PDF"
        description="Remove password protection. Pages are rasterized into a fresh, unrestricted PDF."
      />

      {!file ? (
        <Dropzone accept="application/pdf" onFiles={(f) => setFile(f[0])} />
      ) : (
        <Card className="space-y-3 max-w-lg">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium truncate">{file.name}</div>
            <Button size="sm" variant="ghost" onClick={() => { setFile(null); setPassword(''); setErr(''); }}>
              Change
            </Button>
          </div>
          <Field label="Password (if required)">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Leave empty if owner-only restrictions"
              onKeyDown={(e) => {
                if (e.key === 'Enter') unlock();
              }}
            />
          </Field>
          {err && (
            <div className="flex items-center gap-2 text-xs text-danger">
              <AlertCircle size={12} />
              {err}
            </div>
          )}
          <Button variant="primary" className="w-full" loading={working} onClick={unlock}>
            <Unlock size={12} />
            Unlock and download
          </Button>
          <div className="text-2xs text-fg-subtle leading-relaxed">
            This rasterizes the document. Text becomes non-searchable but the resulting file has no restrictions.
          </div>
        </Card>
      )}
    </div>
  );
}

export default PdfUnlock;
